import { z } from "zod";

export const uuidSchema = z.string().uuid();
export const currencySchema = z.string().regex(/^[A-Z]{3}$/);
export const moneyMinorSchema = z.string().regex(/^-?\d+$/);
export const nonNegativeMoneyMinorSchema = z.string().regex(/^\d+$/);
export const householdDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const instantSchema = z.string().datetime({ offset: true });
export const opaqueCursorSchema = z.string().min(1).max(512);

export const confidenceLevelSchema = z.enum(["confirmed", "high", "medium", "low", "unknown"]);
export const sourceTypeSchema = z.enum([
  "manual",
  "csv",
  "provider",
  "user_rule",
  "inferred",
  "calculated",
]);
export const transactionStatusSchema = z.enum(["pending", "posted", "removed", "reversed"]);
export const transactionKindSchema = z.enum([
  "inflow",
  "purchase",
  "transfer",
  "refund",
  "reversal",
  "fee",
  "interest",
  "cash",
  "payment",
  "unknown",
]);
export const purposeTypeSchema = z.enum([
  "bills",
  "groceries_free_money",
  "hockey_school",
  "emergency",
  "pressure_relief",
  "debt",
  "income_landing",
  "custom",
]);
export const coverageStateSchema = z.enum([
  "covered",
  "partially_covered",
  "at_risk",
  "unconfirmed",
  "paid",
  "skipped_changed",
]);
export const recommendationPrioritySchema = z.enum([
  "critical",
  "protect",
  "clarify",
  "reduce_pressure",
  "improve",
  "none",
]);
export const recommendationStatusSchema = z.enum([
  "active",
  "completed",
  "deferred",
  "rejected",
  "superseded",
  "expired",
]);
export const affordabilityOutcomeSchema = z.enum([
  "safe_now",
  "safe_with_tradeoff",
  "wait_until",
  "not_safe_current_plan",
  "cannot_determine",
]);

export const freshnessSchema = z.object({
  asOf: instantSchema,
  isStale: z.boolean(),
  confidence: confidenceLevelSchema,
  limitations: z.array(z.string()),
});

export const evidenceItemSchema = z.object({
  entityType: z.string().min(1),
  entityId: uuidSchema.optional(),
  label: z.string().min(1),
  value: z.string().optional(),
  source: sourceTypeSchema,
  observedAt: instantSchema.optional(),
  confidence: confidenceLevelSchema,
});

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.enum([
      "AUTH_REQUIRED",
      "RECENT_AUTH_REQUIRED",
      "FORBIDDEN",
      "NOT_FOUND",
      "VALIDATION_FAILED",
      "CONFLICT",
      "IDEMPOTENCY_CONFLICT",
      "RATE_LIMITED",
      "CONNECTION_REAUTH_REQUIRED",
      "CONNECTION_PROVIDER_UNAVAILABLE",
      "SYNC_IN_PROGRESS",
      "DATA_STALE",
      "CALCULATION_IN_PROGRESS",
      "CALCULATION_FAILED",
      "CANNOT_DETERMINE",
      "UNSUPPORTED_ACCOUNT",
      "PROVIDER_DISABLED",
      "IMPORT_FAILED",
      "INTERNAL_ERROR",
    ]),
    message: z.string(),
    requestId: uuidSchema,
    retryable: z.boolean(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});

export const apiEnvelopeSchema = <T extends z.ZodTypeAny>(data: T) =>
  z.object({
    requestId: uuidSchema,
    calculationRunId: uuidSchema.optional(),
    freshness: freshnessSchema.optional(),
    data,
  });

export const householdSettingsInputSchema = z.object({
  timezone: z.string().min(1),
  baseCurrency: currencySchema,
  financialHorizonDays: z.number().int().min(1).max(366),
  minimumActionMinor: nonNegativeMoneyMinorSchema,
  householdBufferMinor: nonNegativeMoneyMinorSchema,
  staleAfterMinutes: z.number().int().min(5).max(43_200),
  debtStrategy: z.enum(["avalanche", "snowball", "custom"]),
  notifications: z.object({
    inApp: z.literal(true),
    email: z.boolean(),
    push: z.boolean(),
  }),
});

export const accountInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  type: z.enum(["checking", "savings", "credit", "loan", "cash", "other"]),
  subtype: z.string().trim().max(80).optional(),
  currentBalanceMinor: moneyMinorSchema,
  availableBalanceMinor: moneyMinorSchema.optional(),
  currency: currencySchema,
  purposeId: uuidSchema.optional(),
  protectionLevel: z.enum(["none", "standard", "protected", "emergency_only"]),
});

export const manualTransactionInputSchema = z.object({
  accountId: uuidSchema,
  amountMinor: moneyMinorSchema.refine((value) => value !== "0", "Amount cannot be zero"),
  currency: currencySchema,
  householdDate: householdDateSchema,
  authorizedAt: instantSchema.optional(),
  postedAt: instantSchema.optional(),
  status: transactionStatusSchema,
  kind: transactionKindSchema,
  description: z.string().trim().min(1).max(300),
  merchant: z.string().trim().max(180).optional(),
  canonicalCategory: z.string().trim().max(120).optional(),
  purposeId: uuidSchema.optional(),
  obligationOccurrenceId: uuidSchema.optional(),
  note: z.string().trim().max(1_000).optional(),
});

export const transactionCorrectionInputSchema = z.object({
  kind: transactionKindSchema.optional(),
  purposeId: uuidSchema.nullable().optional(),
  canonicalCategory: z.string().trim().max(120).optional(),
  obligationOccurrenceId: uuidSchema.nullable().optional(),
  isExcludedFromSpark: z.boolean().optional(),
  ruleScope: z.enum(["transaction", "future", "historical_and_future"]).default("transaction"),
  reason: z.string().trim().max(500).optional(),
});

export const csvColumnMappingSchema = z
  .object({
    date: z.string().min(1),
    description: z.string().min(1),
    amount: z.string().optional(),
    debit: z.string().optional(),
    credit: z.string().optional(),
    status: z.string().optional(),
    category: z.string().optional(),
    balance: z.string().optional(),
  })
  .superRefine((mapping, context) => {
    if (!mapping.amount && !mapping.debit && !mapping.credit) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Map amount or debit/credit columns",
      });
    }
  });

export const csvPreviewRequestSchema = z.object({
  accountId: uuidSchema,
  importSessionId: uuidSchema.optional(),
  fileName: z.string().min(1).max(255),
  fileSize: z.number().int().positive().max(20_000_000),
  fileHash: z.string().regex(/^[a-f0-9]{64}$/),
  encoding: z.enum(["utf-8", "utf-8-bom", "utf-16le", "utf-16be"]),
  delimiter: z.enum([",", ";", "\t", "|"]),
  dateFormat: z.enum(["auto", "yyyy-mm-dd", "mm/dd/yyyy", "dd/mm/yyyy"]),
  decimalSeparator: z.enum([".", ","]),
  mapping: csvColumnMappingSchema.optional(),
  rows: z.array(z.record(z.string(), z.string())).max(50_000),
});

export const csvRowDecisionSchema = z.object({
  rowNumber: z.number().int().positive(),
  action: z.enum(["import", "skip"]),
  normalizedTransaction: manualTransactionInputSchema.optional(),
});

export const csvConfirmRequestSchema = z.object({
  importSessionId: uuidSchema,
  fileHash: z.string().regex(/^[a-f0-9]{64}$/),
  expectedPreviewVersion: z.number().int().positive(),
  decisions: z.array(csvRowDecisionSchema).max(50_000),
});

export const incomeSourceInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  type: z.enum(["salary", "spark", "bonus", "refund", "other"]),
  cadence: z.enum(["weekly", "biweekly", "semimonthly", "monthly", "irregular", "manual"]),
  expectedAmountMinor: nonNegativeMoneyMinorSchema.optional(),
  expectedMinMinor: nonNegativeMoneyMinorSchema.optional(),
  expectedMaxMinor: nonNegativeMoneyMinorSchema.optional(),
  landingAccountId: uuidSchema.optional(),
  confidence: confidenceLevelSchema,
});

export const incomeEventInputSchema = z.object({
  sourceId: uuidSchema,
  expectedDate: householdDateSchema.optional(),
  expectedStartDate: householdDateSchema.optional(),
  expectedEndDate: householdDateSchema.optional(),
  expectedAmountMinor: nonNegativeMoneyMinorSchema.optional(),
  expectedMinMinor: nonNegativeMoneyMinorSchema.optional(),
  expectedMaxMinor: nonNegativeMoneyMinorSchema.optional(),
  confidence: confidenceLevelSchema,
  isProvisional: z.boolean(),
});

export const obligationInputSchema = z.object({
  name: z.string().trim().min(1).max(160),
  payee: z.string().trim().max(160).optional(),
  type: z.enum(["bill", "debt_minimum", "groceries", "hockey_school", "subscription", "other"]),
  recurrence: z.object({
    frequency: z.enum(["once", "weekly", "biweekly", "monthly", "yearly", "custom"]),
    interval: z.number().int().min(1).max(365),
    anchorDate: householdDateSchema,
    customDates: z.array(householdDateSchema).max(100).optional(),
  }),
  amountMethod: z.enum([
    "fixed",
    "last_amount",
    "rolling_median",
    "range_high",
    "user_confirmed",
    "manual",
  ]),
  expectedAmountMinor: nonNegativeMoneyMinorSchema.optional(),
  expectedMinMinor: nonNegativeMoneyMinorSchema.optional(),
  expectedMaxMinor: nonNegativeMoneyMinorSchema.optional(),
  purposeId: uuidSchema,
  priority: z.number().int().min(1).max(100),
  flexibility: z.enum(["fixed", "flexible", "pay_when_funded"]),
  sourceAccountId: uuidSchema.optional(),
  splitAllowed: z.boolean(),
  confidence: confidenceLevelSchema,
});

export const sparkEntryInputSchema = z.object({
  workDate: householdDateSchema,
  confirmedEarningsMinor: nonNegativeMoneyMinorSchema,
  unconfirmedTipsMinor: nonNegativeMoneyMinorSchema.default("0"),
  hoursMinutes: z.number().int().min(0).max(1_440).optional(),
  note: z.string().trim().max(1_000).optional(),
});

export const lifeDayInputSchema = z.object({
  lifeDate: householdDateSchema,
  type: z.enum(["family_caregiving", "school_hockey", "illness", "travel_weather", "other"]),
  capacityFraction: z.number().min(0).max(1),
  expenseMinor: nonNegativeMoneyMinorSchema.optional(),
  expensePurposeId: uuidSchema.optional(),
  note: z.string().trim().max(1_000).optional(),
});

export const debtInputSchema = z.object({
  name: z.string().trim().min(1).max(160),
  accountId: uuidSchema.optional(),
  balanceMinor: nonNegativeMoneyMinorSchema,
  statementBalanceMinor: nonNegativeMoneyMinorSchema.optional(),
  aprBasisPoints: z.number().int().min(0).max(100_000).optional(),
  minimumPaymentMinor: nonNegativeMoneyMinorSchema,
  dueDay: z.number().int().min(1).max(31),
  strategyRank: z.number().int().min(1).max(1_000),
  confidence: confidenceLevelSchema,
});

export const goalInputSchema = z.object({
  name: z.string().trim().min(1).max(160),
  type: z.enum([
    "emergency",
    "hockey_school",
    "irregular_expense",
    "debt_payoff",
    "pressure_relief",
    "custom",
  ]),
  targetAmountMinor: nonNegativeMoneyMinorSchema,
  targetDate: householdDateSchema.optional(),
  priority: z.number().int().min(1).max(100),
  protectedFloorMinor: nonNegativeMoneyMinorSchema,
  note: z.string().trim().max(1_000).optional(),
});

export const affordabilityRequestSchema = z
  .object({
    amountMinor: nonNegativeMoneyMinorSchema.refine(
      (value) => value !== "0",
      "Amount must be positive",
    ),
    sourceAccountId: uuidSchema,
    purchaseDate: householdDateSchema,
    importance: z.enum(["essential", "important", "optional"]),
    flexibility: z.enum(["fixed", "can_wait", "can_split", "alternate_source"]),
    horizon: z.enum(["default", "7d", "next_paycheck", "30d", "custom"]),
    customHorizonEndDate: householdDateSchema.optional(),
  })
  .superRefine((value, context) => {
    if (value.horizon === "custom" && !value.customHorizonEndDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["customHorizonEndDate"],
        message: "Choose an end date for the custom horizon",
      });
    }
    if (value.customHorizonEndDate && value.customHorizonEndDate < value.purchaseDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["customHorizonEndDate"],
        message: "The custom horizon must include the purchase date",
      });
    }
  });

export const recommendationOutcomeInputSchema = z.object({
  outcome: z.enum(["completed", "deferred", "rejected", "already_handled"]),
  deferUntil: instantSchema.optional(),
  reason: z.string().trim().max(500).optional(),
  correctionId: uuidSchema.optional(),
});

export const reservationReadSchema = z.object({
  id: uuidSchema,
  label: z.string(),
  dueDate: householdDateSchema.optional(),
  amountMinor: moneyMinorSchema,
  reservedMinor: nonNegativeMoneyMinorSchema,
  state: coverageStateSchema,
  confidence: confidenceLevelSchema,
});

export const recommendationReadSchema = z.object({
  id: uuidSchema,
  type: z.enum([
    "reserve",
    "transfer",
    "pay",
    "confirm",
    "record",
    "pause",
    "allocate",
    "wait",
    "do_nothing",
  ]),
  priority: recommendationPrioritySchema,
  status: recommendationStatusSchema,
  action: z.string(),
  amountMinor: moneyMinorSchema.optional(),
  reason: z.string(),
  whyNow: z.string(),
  protects: z.array(z.string()),
  evidence: z.array(evidenceItemSchema),
});

export const briefingReadSchema = z.object({
  id: uuidSchema,
  currency: currencySchema,
  generatedAt: instantSchema,
  acknowledgedAt: instantSchema.optional(),
  calculationRunId: uuidSchema,
  engineVersion: z.string(),
  ruleSetVersion: z.string(),
  freshness: freshnessSchema,
  todayAction: recommendationReadSchema,
  whatChanged: z.array(
    z.object({
      key: z.string(),
      message: z.string(),
      severity: z.enum(["info", "warning", "urgent"]),
    }),
  ),
  whatGotPaid: z.array(reservationReadSchema),
  whatIsComing: z.array(reservationReadSchema),
  whatIsCovered: z.array(reservationReadSchema),
  eligibleFundsMinor: moneyMinorSchema,
  signedSafeToUseMinor: moneyMinorSchema,
  displaySafeToUseMinor: nonNegativeMoneyMinorSchema,
  spark: z.object({
    minimumTargetMinor: nonNegativeMoneyMinorSchema,
    comfortableTargetMinor: nonNegativeMoneyMinorSchema,
    aggressiveTargetMinor: nonNegativeMoneyMinorSchema,
    confirmedMinor: nonNegativeMoneyMinorSchema,
    remainingDailyAverageMinor: nonNegativeMoneyMinorSchema.optional(),
    workableDays: z.number().int().nonnegative(),
    status: z.enum(["needs_work", "done_required", "planning_problem", "not_configured"]),
  }),
  household: z.object({
    groceriesFreeMoneyMinor: moneyMinorSchema,
    groceryWeekSpentMinor: nonNegativeMoneyMinorSchema,
    protectedPurposeLabels: z.array(z.string()),
    notableActivity: z.array(z.string()),
  }),
  needsAttention: z.array(z.object({ id: uuidSchema, type: z.string(), message: z.string() })),
  limitations: z.array(z.string()),
});

export const affordabilityReadSchema = z.object({
  outcome: affordabilityOutcomeSchema,
  reason: z.string(),
  remainsCovered: z.array(z.string()),
  changes: z.array(z.string()),
  tradeoff: z.string().optional(),
  earliestSaferDate: householdDateSchema.optional(),
  waitForIncomeEventId: uuidSchema.optional(),
  evidence: z.array(evidenceItemSchema),
  calculationRunId: uuidSchema,
});

export const profileReadSchema = z.object({
  userId: uuidSchema,
  email: z.string().email().optional(),
  displayName: z.string().optional(),
  locale: z.string(),
  timezone: z.string(),
  onboardingState: z.enum([
    "profile",
    "accounts",
    "purposes",
    "income",
    "obligations",
    "rules",
    "spark",
    "briefing",
    "complete",
  ]),
  household: z.object({
    id: uuidSchema,
    name: z.string(),
    baseCurrency: currencySchema,
  }),
});

export const accountReadSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  type: z.enum(["checking", "savings", "credit", "loan", "cash", "other"]),
  subtype: z.string().optional(),
  currentBalanceMinor: moneyMinorSchema,
  availableBalanceMinor: moneyMinorSchema.optional(),
  currency: currencySchema,
  balanceAsOf: instantSchema,
  source: z.enum(["manual", "csv", "provider"]),
  purposeId: uuidSchema.optional(),
  purposeName: z.string().optional(),
  protectionLevel: z.enum(["none", "standard", "protected", "emergency_only"]),
  isActive: z.boolean(),
  version: z.number().int().positive(),
});

export const purposeReadSchema = z.object({
  id: uuidSchema,
  type: purposeTypeSchema,
  name: z.string(),
  priority: z.number().int(),
  protectionLevel: z.enum(["none", "standard", "protected", "emergency_only"]),
  protectedFloorMinor: nonNegativeMoneyMinorSchema,
  isActive: z.boolean(),
  version: z.number().int().positive(),
});

export const activityItemReadSchema = z.object({
  id: uuidSchema,
  accountId: uuidSchema,
  accountName: z.string(),
  householdDate: householdDateSchema,
  description: z.string(),
  merchant: z.string().optional(),
  amountMinor: moneyMinorSchema,
  currency: currencySchema,
  status: transactionStatusSchema,
  kind: transactionKindSchema,
  purposeId: uuidSchema.optional(),
  purposeName: z.string().optional(),
  category: z.string().optional(),
  source: z.enum(["manual", "csv", "provider"]),
  confidence: confidenceLevelSchema,
  note: z.string().optional(),
  isExcludedFromSpark: z.boolean(),
  version: z.number().int().positive(),
});

export const activityPageReadSchema = z.object({
  items: z.array(activityItemReadSchema),
  nextCursor: opaqueCursorSchema.optional(),
});

export const coverageReadSchema = z.object({
  horizon: z.enum(["default", "7d", "next_paycheck", "30d", "custom"]),
  horizonStartDate: householdDateSchema,
  horizonEndDate: householdDateSchema,
  currency: currencySchema,
  eligibleFundsMinor: moneyMinorSchema,
  signedSafeToUseMinor: moneyMinorSchema,
  displaySafeToUseMinor: nonNegativeMoneyMinorSchema,
  reservations: z.array(
    z.object({
      id: uuidSchema,
      label: z.string(),
      purposeName: z.string().optional(),
      dueDate: householdDateSchema.optional(),
      amountMinor: moneyMinorSchema,
      reservedMinor: nonNegativeMoneyMinorSchema,
      state: coverageStateSchema,
      confidence: confidenceLevelSchema,
      nextFundingSource: z.string().optional(),
    }),
  ),
  buffers: z.array(
    z.object({
      id: uuidSchema,
      label: z.string(),
      amountMinor: nonNegativeMoneyMinorSchema,
      state: z.enum(["covered", "partially_covered", "at_risk"]),
    }),
  ),
  unknowns: z.array(z.string()),
  evidence: z.array(evidenceItemSchema),
  engineVersion: z.string(),
  calculatedAt: instantSchema,
});

export const csvPreviewReadSchema = z.object({
  importSessionId: uuidSchema,
  previewVersion: z.number().int().positive(),
  fileHash: z.string(),
  detectedEncoding: z.enum(["utf-8", "utf-8-bom", "utf-16le", "utf-16be"]),
  detectedDelimiter: z.enum([",", ";", "\t", "|"]),
  headers: z.array(z.string()),
  suggestedMapping: z.object({
    date: z.string().optional(),
    description: z.string().optional(),
    amount: z.string().optional(),
    debit: z.string().optional(),
    credit: z.string().optional(),
    status: z.string().optional(),
    category: z.string().optional(),
    balance: z.string().optional(),
  }),
  rows: z.array(
    z.object({
      rowNumber: z.number().int().positive(),
      raw: z.record(z.string(), z.string()),
      normalized: manualTransactionInputSchema.optional(),
      status: z.enum(["ready", "warning", "invalid", "duplicate"]),
      warnings: z.array(z.string()),
      duplicateOfTransactionId: uuidSchema.optional(),
    }),
  ),
  totals: z.object({
    rows: z.number().int().nonnegative(),
    ready: z.number().int().nonnegative(),
    warnings: z.number().int().nonnegative(),
    invalid: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
  }),
});

export const csvImportReadSchema = z.object({
  id: uuidSchema,
  fileName: z.string(),
  accountName: z.string(),
  createdAt: instantSchema,
  status: z.enum(["previewed", "importing", "completed", "failed"]),
  importedCount: z.number().int().nonnegative(),
  duplicateCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  failureMessage: z.string().optional(),
});

export const obligationReadSchema = z.object({
  id: uuidSchema,
  occurrenceId: uuidSchema.optional(),
  name: z.string(),
  payee: z.string().optional(),
  type: z.string(),
  nextDueDate: householdDateSchema.optional(),
  expectedAmountMinor: nonNegativeMoneyMinorSchema.optional(),
  currency: currencySchema,
  amountMethod: z.string(),
  purposeName: z.string(),
  sourceAccountName: z.string().optional(),
  priority: z.number().int(),
  flexibility: z.string(),
  splitAllowed: z.boolean(),
  fundingState: coverageStateSchema,
  fundedMinor: nonNegativeMoneyMinorSchema,
  remainingMinor: nonNegativeMoneyMinorSchema,
  version: z.number().int().positive(),
});

export const planReadSchema = z.object({
  currency: currencySchema,
  fromDate: householdDateSchema,
  toDate: householdDateSchema,
  paycheckWindows: z.array(
    z.object({
      id: uuidSchema,
      label: z.string(),
      startDate: householdDateSchema,
      endDate: householdDateSchema,
      incomeLabel: z.string().optional(),
      expectedIncomeMinor: nonNegativeMoneyMinorSchema.optional(),
      incomeConfidence: confidenceLevelSchema.optional(),
      status: z.enum(["covered", "partially_covered", "at_risk", "unconfirmed"]),
      jobs: z.array(
        z.object({
          id: uuidSchema,
          label: z.string(),
          dueDate: householdDateSchema.optional(),
          plannedMinor: nonNegativeMoneyMinorSchema,
          fundedMinor: nonNegativeMoneyMinorSchema,
          state: z.string(),
        }),
      ),
    }),
  ),
  obligations: z.array(obligationReadSchema),
});

export const sparkReadSchema = z.object({
  currency: currencySchema,
  horizonLabel: z.string(),
  minimumTargetMinor: nonNegativeMoneyMinorSchema,
  comfortableTargetMinor: nonNegativeMoneyMinorSchema,
  aggressiveTargetMinor: nonNegativeMoneyMinorSchema,
  confirmedEarningsMinor: nonNegativeMoneyMinorSchema,
  unconfirmedTipsMinor: nonNegativeMoneyMinorSchema,
  remainingTargetMinor: nonNegativeMoneyMinorSchema,
  remainingDailyAverageMinor: nonNegativeMoneyMinorSchema.optional(),
  workableDays: z.number().int().nonnegative(),
  estimatedDaysNeeded: z.number().nonnegative().optional(),
  status: z.enum(["needs_work", "done_required", "planning_problem", "not_configured"]),
  progress: z.object({
    minimumPercent: z.number().nonnegative(),
    comfortablePercent: z.number().nonnegative(),
    aggressivePercent: z.number().nonnegative(),
  }),
  nextDepositJobs: z.array(
    z.object({
      id: uuidSchema,
      label: z.string(),
      plannedMinor: nonNegativeMoneyMinorSchema,
    }),
  ),
  entries: z.array(
    z.object({
      id: uuidSchema,
      workDate: householdDateSchema,
      confirmedEarningsMinor: nonNegativeMoneyMinorSchema,
      unconfirmedTipsMinor: nonNegativeMoneyMinorSchema,
      hoursMinutes: z.number().int().nonnegative().optional(),
      reconciliationStatus: z.string(),
      note: z.string().optional(),
    }),
  ),
  lifeDays: z.array(
    z.object({
      id: uuidSchema,
      lifeDate: householdDateSchema,
      type: z.string(),
      capacityFraction: z.number().min(0).max(1),
      note: z.string().optional(),
    }),
  ),
  evidence: z.array(evidenceItemSchema),
});

export const goalReadSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  type: z.string(),
  targetAmountMinor: nonNegativeMoneyMinorSchema,
  fundedAmountMinor: nonNegativeMoneyMinorSchema,
  protectedAmountMinor: nonNegativeMoneyMinorSchema,
  currency: currencySchema,
  targetDate: householdDateSchema.optional(),
  priority: z.number().int(),
  progressPercent: z.number().nonnegative(),
  nextAction: z.string().optional(),
  projection: z.string().optional(),
  version: z.number().int().positive(),
});

export const debtReadSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  balanceMinor: nonNegativeMoneyMinorSchema,
  currency: currencySchema,
  aprBasisPoints: z.number().int().nonnegative().optional(),
  minimumPaymentMinor: nonNegativeMoneyMinorSchema,
  dueDay: z.number().int().min(1).max(31),
  strategyRank: z.number().int().positive(),
  confidence: confidenceLevelSchema,
  nextAction: z.string().optional(),
  version: z.number().int().positive(),
});

export const incomeEventReadSchema = z.object({
  id: uuidSchema,
  sourceName: z.string(),
  type: z.enum(["salary", "spark", "bonus", "refund", "other"]),
  expectedDate: householdDateSchema.optional(),
  expectedAmountMinor: nonNegativeMoneyMinorSchema.optional(),
  expectedMinMinor: nonNegativeMoneyMinorSchema.optional(),
  expectedMaxMinor: nonNegativeMoneyMinorSchema.optional(),
  actualAmountMinor: nonNegativeMoneyMinorSchema.optional(),
  currency: currencySchema,
  confidence: confidenceLevelSchema,
  status: z.enum(["expected", "confirmed", "posted", "late", "cancelled"]),
  isProvisional: z.boolean(),
  plannedJobs: z.array(
    z.object({
      label: z.string(),
      plannedMinor: nonNegativeMoneyMinorSchema,
      status: z.string(),
    }),
  ),
  version: z.number().int().positive(),
});

export const needsConfirmationReadSchema = z.object({
  items: z.array(
    z.object({
      id: uuidSchema,
      type: z.enum(["recurring_bill", "transfer", "income", "bill_change", "spark_deposit"]),
      title: z.string(),
      description: z.string(),
      confidence: confidenceLevelSchema,
      impact: z.string(),
      observedAt: instantSchema,
      options: z.array(z.enum(["approve", "edit", "dismiss", "confirm", "reject"])),
    }),
  ),
});

export const settingsReadSchema = z.object({
  profile: z.object({
    displayName: z.string().optional(),
    timezone: z.string(),
    locale: z.string(),
  }),
  household: z.object({
    name: z.string(),
    baseCurrency: currencySchema,
    financialHorizonDays: z.number().int().positive(),
    householdBufferMinor: nonNegativeMoneyMinorSchema,
    minimumActionMinor: nonNegativeMoneyMinorSchema,
    debtStrategy: z.enum(["avalanche", "snowball", "custom"]),
  }),
  sparkPlanning: z
    .object({
      isConfigured: z.boolean(),
      minimumBufferMinor: nonNegativeMoneyMinorSchema,
      comfortableIncrementMinor: nonNegativeMoneyMinorSchema,
      aggressiveIncrementMinor: nonNegativeMoneyMinorSchema,
      expectedNetPerDayMinor: nonNegativeMoneyMinorSchema,
      usualWorkdays: z.array(z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"])),
      tipsTreatment: z.enum(["exclude_until_confirmed", "count_when_confirmed"]),
    })
    .optional(),
  privacy: z.object({
    analyticsEnabled: z.boolean(),
    dataRetentionSummary: z.string(),
  }),
  notifications: z.object({
    inApp: z.literal(true),
    email: z.boolean(),
    push: z.boolean(),
  }),
  featureFlags: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      enabled: z.boolean(),
      description: z.string(),
    }),
  ),
  supportCode: z.string(),
  version: z.number().int().positive(),
});

export const exportReadSchema = z.object({
  id: uuidSchema,
  status: z.enum(["requested", "collecting", "generating", "ready", "expired", "failed"]),
  expiresAt: instantSchema.optional(),
  downloadUrl: z.string().url().optional(),
});

export const domainEventTypeSchema = z.enum([
  "connection.created",
  "connection.reauth_required",
  "connection.sync_requested",
  "connection.sync_completed",
  "connection.disconnected",
  "transactions.changed",
  "import.completed",
  "liabilities.changed",
  "correction.created",
  "obligation.changed",
  "income_event.changed",
  "account.changed",
  "spark_entry.changed",
  "life_day.changed",
  "rules.changed",
  "calculation.requested",
  "calculation.completed",
  "recommendation.outcome_recorded",
  "briefing.acknowledged",
]);

export const domainEventSchema = z.object({
  eventId: uuidSchema,
  eventType: domainEventTypeSchema,
  schemaVersion: z.literal(1),
  aggregateType: z.string().min(1),
  aggregateId: uuidSchema,
  householdId: uuidSchema,
  actorType: z.enum(["user", "system"]),
  actorId: uuidSchema.optional(),
  correlationId: uuidSchema,
  dedupeKey: z.string().min(1).max(300),
  occurredAt: instantSchema,
  payload: z.record(z.string(), z.unknown()),
});

export const jobSchema = z.object({
  jobId: uuidSchema,
  jobType: z.enum(["financial_sync", "domain_recalculate", "notification", "outbox_dispatch"]),
  entityType: z.string().min(1),
  entityId: uuidSchema,
  householdId: uuidSchema.optional(),
  correlationId: uuidSchema,
  attempt: z.number().int().min(0).max(12),
  requestedAt: instantSchema,
  schemaVersion: z.literal(1),
  reason: z.string().max(200).optional(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;
export type ManualTransactionInput = z.infer<typeof manualTransactionInputSchema>;
export type CsvPreviewRequest = z.infer<typeof csvPreviewRequestSchema>;
export type CsvConfirmRequest = z.infer<typeof csvConfirmRequestSchema>;
export type AccountInput = z.infer<typeof accountInputSchema>;
export type IncomeSourceInput = z.infer<typeof incomeSourceInputSchema>;
export type ObligationInput = z.infer<typeof obligationInputSchema>;
export type SparkEntryInput = z.infer<typeof sparkEntryInputSchema>;
export type LifeDayInput = z.infer<typeof lifeDayInputSchema>;
export type DebtInput = z.infer<typeof debtInputSchema>;
export type GoalInput = z.infer<typeof goalInputSchema>;
export type AffordabilityRequest = z.infer<typeof affordabilityRequestSchema>;
export type RecommendationOutcomeInput = z.infer<typeof recommendationOutcomeInputSchema>;
export type BriefingRead = z.infer<typeof briefingReadSchema>;
export type AffordabilityRead = z.infer<typeof affordabilityReadSchema>;
export type ProfileRead = z.infer<typeof profileReadSchema>;
export type AccountRead = z.infer<typeof accountReadSchema>;
export type PurposeRead = z.infer<typeof purposeReadSchema>;
export type ActivityItemRead = z.infer<typeof activityItemReadSchema>;
export type ActivityPageRead = z.infer<typeof activityPageReadSchema>;
export type CoverageRead = z.infer<typeof coverageReadSchema>;
export type CsvPreviewRead = z.infer<typeof csvPreviewReadSchema>;
export type CsvImportRead = z.infer<typeof csvImportReadSchema>;
export type ObligationRead = z.infer<typeof obligationReadSchema>;
export type PlanRead = z.infer<typeof planReadSchema>;
export type SparkRead = z.infer<typeof sparkReadSchema>;
export type GoalRead = z.infer<typeof goalReadSchema>;
export type DebtRead = z.infer<typeof debtReadSchema>;
export type IncomeEventRead = z.infer<typeof incomeEventReadSchema>;
export type NeedsConfirmationRead = z.infer<typeof needsConfirmationReadSchema>;
export type SettingsRead = z.infer<typeof settingsReadSchema>;
export type ExportRead = z.infer<typeof exportReadSchema>;
export type DomainEvent = z.infer<typeof domainEventSchema>;
export type JobMessage = z.infer<typeof jobSchema>;
